import { useState, useEffect } from "react";
import { useAuth } from "../../../../context/authContext";
import createAxiosInstance from "../../../../utils/axiosConfig";
import { save_score, get_framework_options } from "../../../../utils/endpoints";
import { Question, QuestionFlow } from "../../../../utils/interfaces";
import NextArrowIcon from "../../../../assets/svg/nextArrowIcon";

const Questions = ({
	selectedFlow,
	questions,
	handleQuit,
	handleAlert,
	frameworkId,
}: {
	selectedFlow: QuestionFlow;
	questions: Question[];
	handleQuit: () => void;
	frameworkId: number;
	handleAlert: (alert: {
		message: string;
		show: boolean;
		type: string;
	}) => void;
}) => {
	const { userId, logout } = useAuth();
	const [loading, setLoading] = useState<boolean>(false);
	const [scoringOptions, setScoringOptions] = useState<any[]>([]);
	const [optionScores, setOptionScores] = useState<{ [key: string]: number }>({});
	const [tooltipContent, setTooltipContent] = useState<{ [key: string]: { [value: string]: string[] } }>({});

	// Fetch scoring options when component mounts
	useEffect(() => {
		const fetchScoringOptions = async () => {
			if (!frameworkId) return;

			try {
				const response = await createAxiosInstance().get(
					get_framework_options(frameworkId)
				);
				
				if (response.status === 200) {
					console.log('🎯 Scoring Options for Framework:', response.data);
					const options = response.data.data || response.data;
					setScoringOptions(options);

					// Transform the data into the correct format
					if (options && options.length > 0) {
						// Create option scores object: {optionName: score}
						const optionScoresObj = options.reduce((acc: any, option: any) => {
							acc[option.option_name] = option.score;
							return acc;
						}, {});

						// Create tooltip content object: {optionName: tooltipContent}
						const tooltipContentObj = options.reduce((acc: any, option: any) => {
							acc[option.option_name] = option.tooltip_content;
							return acc;
						}, {});

						console.log('🎯 Transformed Option Scores:', optionScoresObj);
						console.log('🎯 Transformed Tooltip Content:', tooltipContentObj);

						// Update the option scores and tooltip content
						setOptionScores(optionScoresObj);
						setTooltipContent(tooltipContentObj);
					}
				}
			} catch (error) {
				console.error('Error fetching scoring options:', error);
			}
		};

		fetchScoringOptions();
	}, [frameworkId]);

	const [qaPairs, setQaPairs] = useState<{ [key: string]: string }>({});
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
	// const optionScores: { [key: string]: number } = {
	// 	None: 0,
	// 	Partial: 1,
	// 	"Risk Informed": 2,
	// 	Repeatable: 3,
	// 	Adaptive: 4,
	// };
	// const optionScoresRansomware: { [key: string]: number } = {
	// 	"Initial/ad hoc": 1,
	// 	Developing: 2,
	// 	Defined: 3,
	// 	"Managed and measurable": 4,
	// 	Optimized: 5,
	// };
	// const optionScoresNistPrivacy: {[key: string]: number} = {
	// 	None: 0,
	// 	Initial: 1,
	// 	Developing: 2,
	// 	Defined: 3,
	// 	Adaptive: 4,
	// 	Optimized: 5,
	// 	"Does Not Apply": 6,
	// }
	
	const [showConstraintQuestion, setShowConstraintQuestion] = useState(false);
	const [showConstraintInput, setShowConstraintInput] = useState(false);
	const [userConstraint, setUserConstraint] = useState<string>("");
	const [constraintResponse, setConstraintResponse] = useState<string>("");

	const calculateTopBarWidth = (
		currentIndex: number,
		totalQuestions: number
	) => {
		if (totalQuestions === 0) return "0%";
		return `${((currentIndex + 1) / totalQuestions) * 98}%`;
	};

	const handleOptionChange = (option: string) => {
		const currentQuestion = questions[currentQuestionIndex];
		setQaPairs((prevQaPairs) => ({
			...prevQaPairs,
			[currentQuestion.question_text]: option,
		}));
	};

	const showAlert = () => {
		handleAlert({
			message: "Please ensure you have made a selection.",
			show: true,
			type: "warning",
		});
	};

	const hideAlert = () => {
		handleAlert({
			message: "",
			show: false,
			type: "",
		});
	};

	const handleNext = async () => {
		if (
			currentQuestionIndex < questions.length - 1 &&
			Object.keys(qaPairs).length === currentQuestionIndex + 1
		) {
			const currentQuestion = questions[currentQuestionIndex];
			const selectedOption = qaPairs[currentQuestion?.question_text];
			console.log(selectedOption, "selected option", currentQuestion);
			console.log(selectedFlow?.domain_id 
				, "hehe");
			// store answer in DB...
			try {
				await createAxiosInstance().post("/user_answers", {
					user_id: userId,
					question_id: currentQuestion?.id,
					answer_key: optionScores[selectedOption],
					attempt_key: 1,
				});
			} catch (e: any) {
				if (e.status === 401 || e.status == 403) {
					logout();
				}
				console.log(e);
			} finally {
				setCurrentQuestionIndex(currentQuestionIndex + 1);
				hideAlert();
			}
		} else {
			console.log("here now");
			if (
				Object.keys(qaPairs).length === questions.length ||
				currentQuestionIndex < Object.keys(qaPairs).length
			) {
				const currentQuestion = questions[currentQuestionIndex];
				const selectedOption = qaPairs[currentQuestion?.question_text];
				// store answer in DB...

				try {
					await createAxiosInstance().post("/user_answers", {
						user_id: userId,
						question_id: currentQuestion?.id,
						answer_key: optionScores[selectedOption],
						attempt_key: 1,
					});
				} catch (e) {
					console.log(e);
				} finally {
					if (
						currentQuestionIndex + 1 === questions.length &&
						!showConstraintInput
					) {
						setShowConstraintQuestion(true);
					} else {
						setCurrentQuestionIndex(currentQuestionIndex + 1);
						hideAlert();
					}
				}
			}

			if (Object.keys(qaPairs).length < currentQuestionIndex + 1) {
				// show alert as user has not selected any option...
				console.log("showing alert");
				showAlert();
			}
		}
	};

	const handlePrev = () => {
		if (currentQuestionIndex > 0 && !showConstraintQuestion) {
			setCurrentQuestionIndex(currentQuestionIndex - 1);
		} else {
			setShowConstraintQuestion(false);
			setShowConstraintInput(false);
			setUserConstraint("");
			setConstraintResponse("");
		}
	};

	const handleSubmit = async () => {
		setLoading(true);
		const currentQuestion = questions[currentQuestionIndex];
		const selectedOption = qaPairs[currentQuestion?.question_text];

		try {
			await createAxiosInstance().post("/user_answers", {
				user_id: userId,
				question_id: currentQuestion?.id,
				answer_key: optionScores[selectedOption],
				attempt_key: 1,
			});
		} catch (e) {
			console.log(e);
		} finally {
			let totalScore = 0;
			let questionsCount = questions.length;
			Object.values(qaPairs).forEach((selectedOption) => {
				if(selectedOption !== "Does Not Apply"){
					(totalScore += optionScores[selectedOption] || 0)
				}else{
					questionsCount -= 1;
				}
			});
			
			try {
				const response = await createAxiosInstance().post(save_score, {
					score: totalScore / questionsCount || 0,
					flow_id: selectedFlow.id,
					user_id: userId,
				});

				setLoading(false);

				if (response.status == 401 || response.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access
					console.log("user token expired");
					logout();
					window.location.href = "/";
				}else{
					localStorage.setItem(
						`${selectedFlow.id}`,
						JSON.stringify(response.data)
					);
					// Save user_constraint separately
					if (userConstraint) {
						await createAxiosInstance().post("/user_constraints", {
							user_id: userId,
							flow_id: selectedFlow.id,
							selected_option: constraintResponse === "Yes",
							constraint_text: userConstraint,
						});
					}
					handleQuit();
				}
			} catch (e: any) {
				console.log(e, "error");
			} finally {
				setLoading(false);
			}
		}
	};

	const handleConstraintResponse = (response: string) => {
		setConstraintResponse(response);
		if (response === "Yes") {
			setShowConstraintInput(true);
		} else {
			setShowConstraintInput(false);
			setUserConstraint("None");
		}
	};

	const getOptions = () => {
		return Object.keys(tooltipContent);
	};

	const options = getOptions();
	return (
		<div>
			<div className="offcanvas-backdrop fade show"
				onClick={handleQuit}>
			</div>
				<div className="offcanvas offcanvas-end show w-55"
					tabIndex={-1}
					id="offcanvasExample"
					aria-labelledby="offcanvasExampleLabel">
					<div className="offcanvas-header">
						<h5 className="offcanvas-title" id="offcanvasExampleLabel" style={{
							fontSize: '1.25rem',
							fontWeight: 'bold',
							marginLeft: '3rem',
							padding: '10px 0px'
						}}>
							QUESTIONNAIRE
						</h5>
						<button
							type="button"
							className="btn-close"
							data-bs-dismiss="offcanvas"
							aria-label="Close"
							onClick={handleQuit}
						></button>
					</div>

					<div className="offcanvas-body scroller">
						
						<div className="card-body">
							<h2 className="my-3 my-md-4" style={{
								fontSize: "1.5rem",
								fontWeight: "bold",
								color: "#003C9E",
								marginTop: "0.5rem"
							}}> {selectedFlow.title}</h2>

							<div className="mx-5">
							<div
								style={{
									borderTop: "8px solid #5376F0",
									width: calculateTopBarWidth(
										currentQuestionIndex,
										questions.length
									),
									marginLeft: "0%",
									marginBottom: "1rem",
									justifyContent: "center",
								}}
							></div>
								{showConstraintQuestion ? (
									<>
										<h3>
											Are there any constraints which you would like to specify?
										</h3>
										<ul className="list-group checklist pb-4">
											{["Yes", "No"].map((option, index) => (
												<li className="list-group-item" key={option}>
													<input
														className="form-check-input me-1"
														type="radio"
														name="listGroupRadio"
														id={`option${index}`}
														onChange={() => handleConstraintResponse(option)}
													/>
													<label
														className="form-check-label"
														htmlFor={`option${index}`}
													>
														{option}
													</label>
												</li>
											))}
										</ul>

										{showConstraintInput && (
											<div className="form-floating mb-3 mt-3">
												<textarea
													rows={15}
													className="form-control"
													placeholder="Specify your constraints here..."
													value={userConstraint}
													onChange={(e) => setUserConstraint(e.target.value)}
												/>
												<label htmlFor="floatingInput">
													Specify your constraints here...
												</label>
											</div>
										)}
									</>
								) : (
									<>
										<div style={{ 
											display: "grid",
											gridTemplateRows: "auto 1fr",
											height: "70vh",
											gap: "2rem"
										}}>
											{/* Question Text */}
											<div style={{ 
												minHeight: "10vh",
												overflow: "visible"
											}}>
												<p
													className="text-start"
													style={{
														fontSize: "1.5rem",
														margin: 0
													}}
												>
													<span
														className=""
														style={{ fontSize: "1.3rem", fontWeight: "bold", color: "#5376F0" }}
													>{`${selectedFlow.description}-0${questions[currentQuestionIndex]?.order}: `}</span>
													{questions[currentQuestionIndex]?.question_text}
												</p>
											</div>

											{/* Answer Options */}
											<div style={{ 
												display: "flex",
												flexDirection: "column",
												justifyContent: "space-evenly",
												overflow: "visible",
												paddingBottom: "1rem",
												position: "relative",
												zIndex: 1
											}}>
												{options.map((option) => (
													<div
														key={`option_${option}_item`}
														className="list-group-item gap-3"
														style={{
															marginRight: '10%',
															flex: "0 0 auto"
														}}
													>
													<button
														className={`btn btn-question me-4 ${
															qaPairs[questions[currentQuestionIndex]?.question_text] === option 
																? "selected-option" 
																: "unselected-option"
														}`}
														onClick={() => handleOptionChange(option)}
													>
														{option}
													</button>
													{tooltipContent && 
													 tooltipContent[option as keyof typeof tooltipContent] && 
													 Object.keys(tooltipContent[option as keyof typeof tooltipContent]).length > 0 && (
														<div className="tooltip-container">
															<i
																className="bi bi-info-circle-fill text-secondary cursor-pointer"
																style={{ cursor: "pointer" }}
															></i>
															<div className="tooltip-content px-4">
															{(() => {
																const content = tooltipContent[option as keyof typeof tooltipContent];
																const keys = Object.keys(content);
																
																// Check if it's a single key structure
																if (keys.length === 1) {
																	const singleKey = keys[0];
																	const value = content[singleKey as keyof typeof content];
																	return (
																		<>
																			<h5>{singleKey}</h5>
																			{Array.isArray(value) ? (
																				value.map((sentence: string, i: number) => (
																					<p key={i}>{sentence}</p>
																				))
																			) : (
																				<p>{value as string}</p>
																			)}
																		</>
																	);
																}
																
																// Otherwise, render two-key structure (governance/management or other pairs)
																return (
																	<>
																		<h5>{keys[0] || ""}</h5>
																		{Array.isArray(content[keys[0] as keyof typeof content]) && 
																			(content[keys[0] as keyof typeof content] as string[]).map((sentence: string, i: number) => (
																				<p key={i}>{sentence}</p>
																			))
																		}
																		{keys[1] && (
																			<>
																				<h5>{keys[1]}</h5>
																				{Array.isArray(content[keys[1] as keyof typeof content]) && 
																					(content[keys[1] as keyof typeof content] as string[]).map((sentence: string, i: number) => (
																						<p key={i}>{sentence}</p>
																					))
																				}
																			</>
																		)}
																	</>
																);
															})()}
														</div>
													</div>
													)}
												</div>
											))}
											</div>
										</div>
									</>
								)}

								<div className="d-flex justify-content-between">
									<div className="mb-3">
										{currentQuestionIndex > 0 && (
											<button
												type="submit"
												className="btn w-10"
												onClick={handlePrev}
												style={{
													color: "#5376F0",
													fontSize: "0.875rem",
													fontWeight: "bold",
												}}
											>
												 <NextArrowIcon style={{
													transform: "rotate(180deg)",
												 }} /> &nbsp; {" "}  Previous
											</button>
										)}
									</div>

									<div className="mb-3">
										<button
											type="submit"
											className="btn w-10"
											onClick={showConstraintQuestion ? handleSubmit : handleNext}
											style={{
												color: "#5376F0",
												fontSize: "0.875rem",
												fontWeight: "bold",
											}}
										>
											{showConstraintQuestion ? "Submit" : "Next"} &nbsp; {" "}  <NextArrowIcon />
										</button>
										
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
		</div>
	);
};

export default Questions;
